import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import ExportButtons from '../components/ExportButtons';

const TABS = [
  ['', 'All'], ['user','User Activity'], ['asset','Asset'], ['incident','Incident'],
  ['ticket','Ticket'], ['task','Task'], ['configuration','Configuration'], ['security','Security'],
  // Google Workspace calls live in their own table (gw_actions) with their own
  // columns, so this tab renders a different view rather than filtering the
  // audit log — mixing them made both harder to read.
  ['gw','Google Workspace'],
];
const COLUMNS = [
  { key:'when',        label:'Date / Time' },
  { key:'user_name',   label:'User' },
  { key:'campus_name', label:'Campus' },
  { key:'module',      label:'Module' },
  { key:'action',      label:'Action' },
  { key:'old_value',   label:'Old Value' },
  { key:'new_value',   label:'New Value' },
  { key:'ip_address',  label:'IP Address' },
];

export default function SystemLogs() {
  const { isAdmin } = useAuth();
  const [tab, setTab] = useState('');
  const [rows, setRows] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [users, setUsers] = useState([]);
  const [actions, setActions] = useState([]);
  const [f, setF] = useState({ campus_id:'', user_id:'', action:'', from:'', to:'', q:'' });
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    if (tab === 'gw') {
      // Different table, different shape — fetched and rendered separately.
      api.get('/gw/actions', { params:{ limit: 1000, failed: f.q === 'failed' ? 1 : undefined } })
        .then(r => setRows(r.data)).catch(()=>setRows([])).finally(()=>setLoading(false));
      return;
    }
    const params = { limit: 1000 };
    if (tab) params.module = tab;
    Object.entries(f).forEach(([k,v]) => { if (v) params[k] = v; });
    api.get('/logs', { params }).then(r => setRows(r.data)).catch(()=>setRows([])).finally(()=>setLoading(false));
  }, [tab, f]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/master/campuses').then(r => setCampuses(r.data)).catch(()=>{});
    api.get('/master/users').then(r => setUsers(r.data)).catch(()=>{});
    api.get('/logs/actions').then(r => setActions(r.data)).catch(()=>{});
  }, []);

  if (!isAdmin) return <div style={{ padding:40, color:'var(--text2)', textAlign:'center' }}>Admin access required</div>;

  const set = (k,v) => setF(p => ({ ...p, [k]:v }));
  const exportRows = rows.map(r => ({ ...r, when: new Date(r.created_at).toLocaleString() }));

  const exportAll = async () => {
    try {
      const res = await api.get('/logs/export-all', { responseType:'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url;
      a.download = `itportal-backup-${new Date().toISOString().slice(0,10)}.json`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch(e) { alert('Export failed'); }
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16, flexWrap:'wrap', gap:10 }}>
        <div>
          <h2 style={{ fontSize:18, fontWeight:600, marginBottom:4 }}>System Logs</h2>
          <p style={{ fontSize:13, color:'var(--text2)' }}>Audit trail across every module — who changed what, when, and from where. Logs are append-only.</p>
        </div>
        <div className="flex-gap" style={{ gap:8 }}>
          <ExportButtons rows={exportRows} columns={COLUMNS} filename="system-logs" title="System Logs" />
          <button className="btn btn-primary btn-sm" onClick={exportAll}>⬇ Export All Data (backup)</button>
        </div>
      </div>

      {/* Category tabs */}
      <div style={{ display:'flex', gap:0, marginBottom:14, borderBottom:'1px solid var(--border)', overflowX:'auto' }}>
        {TABS.map(([id,label]) => (
          <button key={id} onClick={() => setTab(id)}
            style={{ padding:'8px 14px', fontSize:13, cursor:'pointer', background:'transparent', border:'none', whiteSpace:'nowrap',
              borderBottom: tab===id?'2px solid var(--accent)':'2px solid transparent',
              color: tab===id?'var(--accent)':'var(--text2)', fontWeight: tab===id?600:400, marginBottom:-1 }}>
            {label}
          </button>
        ))}
      </div>

      {/* Filters — the Google Workspace tab has its own, much smaller, set */}
      {tab === "gw" ? (
        <div className="flex-gap" style={{ gap:8, marginBottom:14 }}>
          <label className="opt"><input type="checkbox" checked={f.q === "failed"}
            onChange={e => set("q", e.target.checked ? "failed" : "")} /><span>Failed calls only</span></label>
        </div>
      ) : (
      <div className="flex-gap" style={{ gap:8, marginBottom:14, flexWrap:"wrap" }}>
        <input type="date" value={f.from} onChange={e => set('from',e.target.value)} title="From" style={{ padding:'6px 8px' }} />
        <input type="date" value={f.to} onChange={e => set('to',e.target.value)} title="To" style={{ padding:'6px 8px' }} />
        <select value={f.campus_id} onChange={e => set('campus_id',e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All campuses</option>{campuses.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={f.user_id} onChange={e => set('user_id',e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All users</option>{users.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
        </select>
        <select value={f.action} onChange={e => set('action',e.target.value)} style={{ padding:'6px 8px' }}>
          <option value="">All actions</option>{actions.map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input value={f.q} onChange={e => set('q',e.target.value)} placeholder="Search…" style={{ padding:'6px 8px', minWidth:160 }} />
        {(f.from||f.to||f.campus_id||f.user_id||f.action||f.q) && <button className="btn btn-secondary btn-sm" onClick={() => setF({campus_id:'',user_id:'',action:'',from:'',to:'',q:''})}>Clear</button>}
      </div>
      )}

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? <div style={{ padding:40, textAlign:'center', color:'var(--text2)' }}>Loading…</div>
        : !rows.length ? <div className="empty-state"><p>No log entries match.</p></div>
        : tab === 'gw' ? (
          <div className="table-wrap"><table>
            <thead><tr><th>Date / Time</th><th>User</th><th>Campus</th><th>Action</th>
              <th>Target</th><th>Result</th><th>Time</th><th>IP</th></tr></thead>
            <tbody>
              {rows.map(r => {
                // A simulated call is recorded exactly like a real one, so the
                // difference has to be visible or a test run reads as live.
                const sim = String(r.action).startsWith('sim_');
                return (
                  <tr key={r.id}>
                    <td style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                    <td style={{ fontSize:13 }}>{r.user_name || '—'}</td>
                    <td style={{ fontSize:12 }}>{r.campus_name || '—'}</td>
                    <td style={{ fontSize:12, fontWeight:500 }}>
                      {String(r.action).replace(/^sim_/,'').replace(/_/g,' ')}
                      {sim && <span className="badge badge-normal" style={{ marginLeft:6, fontSize:10 }}>simulated</span>}
                    </td>
                    <td className="mono" style={{ fontSize:11.5, maxWidth:240, wordBreak:'break-all' }}>{r.target || '—'}</td>
                    <td style={{ fontSize:12, maxWidth:320 }}>
                      {r.ok
                        ? <span style={{ color:'var(--green)' }}>✓ ok</span>
                        : <span style={{ color:'var(--red)' }}>✗ {r.error || 'failed'}</span>}
                    </td>
                    <td style={{ fontSize:11.5, color:'var(--text2)' }}>{r.ms != null ? `${r.ms} ms` : '—'}</td>
                    <td style={{ fontSize:11, color:'var(--text3)' }}>{(r.ip_address || '').replace('::ffff:','') || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        ) : (
          <div className="table-wrap"><table>
            <thead><tr><th>Date / Time</th><th>User</th><th>Campus</th><th>Module</th><th>Action</th><th>Change</th><th>IP</th></tr></thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id}>
                  <td style={{ fontSize:12, color:'var(--text2)', whiteSpace:'nowrap' }}>{new Date(r.created_at).toLocaleString()}</td>
                  <td style={{ fontSize:13 }}>{r.user_name || '—'}</td>
                  <td style={{ fontSize:12 }}>{r.campus_name || '—'}</td>
                  <td><span className="badge badge-discuss" style={{ fontSize:10 }}>{r.module}</span></td>
                  <td style={{ fontSize:12, fontWeight:500 }}>{r.action}</td>
                  <td style={{ fontSize:12, color:'var(--text2)', maxWidth:280 }}>
                    {r.field_name ? <span>{r.field_name}: </span> : null}
                    {r.old_value ? <span style={{ color:'var(--red)' }}>{r.old_value}</span> : null}
                    {r.old_value && r.new_value ? ' → ' : ''}
                    {r.new_value ? <span style={{ color:'var(--green)' }}>{r.new_value}</span> : (!r.old_value ? '—' : '')}
                  </td>
                  <td style={{ fontSize:11, color:'var(--text3)' }}>{(r.ip_address || '').replace('::ffff:','') || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
      <div style={{ fontSize:11, color:'var(--text3)', marginTop:8 }}>Showing up to 1000 entries. Narrow with filters for older records.</div>
    </div>
  );
}
